// Jarvis AI Chat Page for Iron Forums

import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { jarvisAPI, usersAPI } from '../api/client';
import { useAuth } from '../context/useAuth';
import { roleLabel } from '../utils/roles';
import {
  Send,
  User,
  Sparkles,
  RefreshCw,
  MessageSquare,
  Plus,
  Trash2,
  PanelLeftClose,
  PanelLeftOpen,
  Clock,
  Check,
  Edit,
  ArrowLeft,
  UserCircle,
  CheckCircle,
  ArrowRight,
  X,
  Undo2,
  UserPlus,
} from 'lucide-react';
import type { JarvisConversation, JarvisAction, ProfileFieldsStatus, UndoableAction } from '../types';
import ConfirmModal from '../components/ConfirmModal';
import MicButton from '../components/MicButton';
import AutosizeTextarea from '../components/AutosizeTextarea';
import AskIfMark from '../components/AskIfMark';
import AskIfMemberCards, { membersFromAskIfAction } from '../components/AskIfMemberCards';
import { clearAskIfReturnConversation, setAskIfReturnConversation } from '../utils/askIfReturn';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  action?: JarvisAction;
}

export default function Jarvis() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, refreshUser } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<number | undefined>();
  const [probingQuestions, setProbingQuestions] = useState<string[]>([]);
  const [conversations, setConversations] = useState<JarvisConversation[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true
  );
  const [loadingConversations, setLoadingConversations] = useState(true);

  // Keep sidebar state in sync when crossing lg breakpoint
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handle = () => setSidebarOpen(mq.matches);
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);
  const [pendingAction, setPendingAction] = useState<JarvisAction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  // Invite-a-member form dropped into the chat (admin/facilitator only)
  const [inviteForm, setInviteForm] = useState<JarvisAction | null>(null);
  const [inviteData, setInviteData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'member',
    forum_id: '' as string,
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [hasHandledIncomingMessage, setHasHandledIncomingMessage] = useState(false);
  const [recentActions, setRecentActions] = useState<UndoableAction[]>([]);
  const [showUndoHint, setShowUndoHint] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteConvId, setPendingDeleteConvId] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  
  // Onboarding mode state
  const isOnboardingMode = searchParams.get('mode') === 'onboarding';
  // Admin analytics mode: the org dashboard's "Ask Jarvis" — primes the
  // backend with the org-wide overview (admin-only, enforced server-side).
  const isAnalyticsMode = searchParams.get('mode') === 'analytics';
  const [onboardingFieldsStatus, setOnboardingFieldsStatus] = useState<ProfileFieldsStatus | null>(null);
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  
  // Get incoming message from navigation state (from Dashboard)
  const incomingMessage = location.state?.message as string | undefined;

  const leaveAskIf = (href: string, returnConversationId?: number) => {
    setAskIfReturnConversation(returnConversationId ?? conversationId);
    navigate(href);
  };

  // Explicit exit for mobile, where Layout's nav is minimal on this page.
  // Deep links / fresh tabs have no in-app history to go back to.
  const exitAskIf = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    clearAskIfReturnConversation();
  }, []);

  useEffect(() => {
    // Load conversations and probing questions on mount
    const loadData = async () => {
      try {
        if (isOnboardingMode) {
          // In onboarding mode, load the greeting
          const greetingData = await jarvisAPI.getOnboardingGreeting();
          setOnboardingFieldsStatus(greetingData.fields_status);
          
          // If profile already complete, show a different message
          if (greetingData.profile_completed) {
            setMessages([{
              role: 'assistant',
              content: greetingData.greeting
            }]);
          } else {
            // Show the onboarding greeting
            setMessages([{
              role: 'assistant',
              content: greetingData.greeting
            }]);
          }
          setLoadingConversations(false);
        } else {
          // Normal mode - load conversations and probing questions
          const [questionsData, conversationsData] = await Promise.all([
            jarvisAPI.getProbingQuestions(),
            jarvisAPI.getConversations(),
          ]);
          setProbingQuestions(questionsData.questions);
          setConversations(conversationsData);
          setLoadingConversations(false);

          // Deep-link: ?conversation=<id> auto-opens that conversation (used by
          // Jarvis proactive reflection push notifications).
          const convParam = searchParams.get('conversation');
          if (convParam) {
            const target = conversationsData.find((c) => String(c.id) === convParam);
            if (target) loadConversation(target);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setLoadingConversations(false);
      }
    };
    loadData();
  }, [isOnboardingMode]);
  
  // Handle incoming message from Dashboard
  useEffect(() => {
    if (incomingMessage && !hasHandledIncomingMessage && !loadingConversations) {
      setHasHandledIncomingMessage(true);
      // Clear the state to prevent re-sending on refresh
      window.history.replaceState({}, document.title);
      // Send the message
      sendMessage(incomingMessage);
    }
  }, [incomingMessage, hasHandledIncomingMessage, loadingConversations]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;

    const userMessage: ChatMessage = { role: 'user', content: messageText };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setPendingAction(null);
    setInviteForm(null);

    try {
      if (isOnboardingMode) {
        // Use onboarding API
        const conversationHistory = messages.map(m => ({
          role: m.role,
          content: m.content
        }));
        
        const response = await jarvisAPI.sendOnboardingMessage(messageText, conversationHistory);
        
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response.response,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        
        // Update fields status
        if (response.fields_status) {
          setOnboardingFieldsStatus(response.fields_status);
        }
        
        // Check if onboarding completed
        if (response.action?.type === 'onboarding_completed') {
          setOnboardingComplete(true);
          // Refresh user to update profile_completed status
          if (refreshUser) {
            await refreshUser();
          }
        }
      } else {
        // Regular chat
        const response = await jarvisAPI.chat(
          messageText,
          conversationId,
          isAnalyticsMode ? 'analytics' : undefined,
        );
        setConversationId(response.conversation_id);

        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: response.response,
          action: response.action,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Handle different action types
        if (response.action) {
          const action = response.action;
          
          // Draft message needs user approval
          if (action.type === 'draft_message') {
            setPendingAction(action);
          }

          // Invite-member form: drop the inline form into the chat
          if (action.type === 'invite_form') {
            setInviteError(null);
            setInviteData({
              email: '',
              first_name: '',
              last_name: '',
              role: action.allowed_roles?.[0] || 'member',
              forum_id: action.forums?.length === 1 ? String(action.forums[0].id) : '',
            });
            setInviteForm(action);
          }
          
          // A conversation was created/used (1:1 send or a new group chat) —
          // deep-link the member straight into the thread so a successful
          // creation is visibly reflected.
          if (
            (action.type === 'group_chat_created' ||
              action.type === 'thread_created' ||
              action.type === 'message_sent') &&
            action.thread_id
          ) {
            leaveAskIf(`/messages?thread=${action.thread_id}`, response.conversation_id);
          }

          // Track undoable actions
          if (action.can_undo) {
            trackAction(action);
          }
          
          // Clear recent actions after successful undo
          if (action.type === 'undo_completed') {
            setRecentActions(prev => prev.filter(a => a.id !== action.action_id));
          }
        }

        // Refresh conversations list to show the new/updated conversation
        const updatedConversations = await jarvisAPI.getConversations();
        setConversations(updatedConversations);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content:
            'I apologize, but I encountered an issue. Please try again or reach out to your facilitator.',
        },
      ]);
    } finally {
      setIsLoading(false);
      // Refocus input so user can keep typing (delay allows React to re-enable the input first)
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleSendDraft = async () => {
    if (!pendingAction || pendingAction.type !== 'draft_message') return;

    setActionLoading(true);
    try {
      const result = await jarvisAPI.executeAction('send_message', {
        recipient_id: pendingAction.recipient_id,
        content: pendingAction.content,
      });

      if (result.success) {
        // Add confirmation message
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `✅ ${result.message}`,
          },
        ]);
        setPendingAction(null);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `❌ Failed to send: ${result.error}`,
          },
        ]);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: '❌ Failed to send message. Please try again.',
        },
      ]);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm) return;

    if (!inviteData.email.trim()) {
      setInviteError('Please enter an email address.');
      return;
    }
    if (inviteForm.forum_required && !inviteData.forum_id) {
      setInviteError('Please choose which Forum to add this member to.');
      return;
    }

    setInviteLoading(true);
    setInviteError(null);
    try {
      const result = await usersAPI.inviteMember({
        email: inviteData.email.trim(),
        first_name: inviteData.first_name.trim() || undefined,
        last_name: inviteData.last_name.trim() || undefined,
        role: inviteData.role,
        forum_id: inviteData.forum_id ? Number(inviteData.forum_id) : null,
      });

      const name = [inviteData.first_name, inviteData.last_name].filter(Boolean).join(' ').trim();
      const who = name ? `${name} (${inviteData.email.trim()})` : inviteData.email.trim();
      let confirmation = `✅ Invitation sent to **${who}**. They'll get an email with a secure link to set their password and finish onboarding.`;
      if (result.setup_url) {
        confirmation += `\n\nSetup link: [${result.setup_url}](${result.setup_url})`;
      }
      setMessages((prev) => [...prev, { role: 'assistant', content: confirmation }]);
      setInviteForm(null);
    } catch (error) {
      const err = error as { response?: { data?: { error?: string; detail?: string; email?: string[] } } };
      const data = err.response?.data;
      const msg =
        data?.error ||
        data?.detail ||
        data?.email?.[0] ||
        'Could not send the invitation. Please try again.';
      setInviteError(msg);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleEditDraft = () => {
    if (!pendingAction || pendingAction.type !== 'draft_message') return;

    // Pre-fill the input with a request to edit
    setInput(`Let me edit that message. Change it to: `);
    setPendingAction(null);
  };

  // Handle undo action
  const handleUndo = async () => {
    if (recentActions.length === 0) return;
    
    setActionLoading(true);
    try {
      // Send "undo" as a message to Jarvis
      sendMessage('undo');
    } catch (error) {
      console.error('Error undoing action:', error);
    } finally {
      setActionLoading(false);
    }
  };

  // Track undoable actions from responses
  const trackAction = (action: JarvisAction) => {
    if (action.can_undo && action.entity_type && action.entity_id) {
      const newAction: UndoableAction = {
        id: action.action_id || Date.now(),
        action_type: action.type,
        entity_type: action.entity_type,
        entity_id: action.entity_id,
        description: action.preview_text || `${action.type} on ${action.entity_type}`,
        created_at: new Date().toISOString(),
        time_ago: 'just now',
        can_undo: true,
      };
      setRecentActions(prev => [newAction, ...prev.slice(0, 9)]);
      setShowUndoHint(true);
      // Hide undo hint after 5 seconds
      setTimeout(() => setShowUndoHint(false), 5000);
    }
  };

  // Quick action suggestions based on context
  const getQuickActions = (): { label: string; action: string }[] => {
    if (isOnboardingMode) return [];
    
    return [
      { label: 'Show my Grow Goals', action: 'show my Grow Goals' },
      { label: 'What can you do?', action: 'what can you do?' },
      { label: 'Find someone to message', action: 'I want to message someone' },
    ];
  };

  const startNewConversation = () => {
    setMessages([]);
    setConversationId(undefined);
    setPendingAction(null);
    setInviteForm(null);
  };

  const loadConversation = async (conv: JarvisConversation) => {
    try {
      setIsLoading(true);
      setPendingAction(null);
      setInviteForm(null);
      const fullConversation = await jarvisAPI.getConversation(conv.id);
      setConversationId(conv.id);
      setMessages(
        (fullConversation.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }))
      );
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteConversation = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setPendingDeleteConvId(id);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDeleteConversation = async () => {
    if (pendingDeleteConvId === null) return;
    const id = pendingDeleteConvId;
    setDeleteConfirmOpen(false);
    setPendingDeleteConvId(null);
    try {
      await jarvisAPI.deleteConversation(id);
      setConversations((prev) => prev.filter((c) => c.id !== id));

      if (conversationId === id) {
        startNewConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString();
  };

  // Group conversations by date
  const groupedConversations = conversations.reduce((acc, conv) => {
    const dateLabel = formatDate(conv.updated_at);
    if (!acc[dateLabel]) acc[dateLabel] = [];
    acc[dateLabel].push(conv);
    return acc;
  }, {} as Record<string, JarvisConversation[]>);

  const renderMessageContent = (content: string) => {
    // Split on markdown links ([text](url)) and bold (**...**). Links match
    // first so bold/italic markers inside a link label stay part of the link.
    const parts = content.split(/(\[.*?\]\(.*?\)|\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
      if (linkMatch) {
        const href = linkMatch[2];
        // Emphasis markers inside the link text (Jarvis cites titles as
        // [*Title*](/library?doc=ID)) render as styling, not literal asterisks.
        const label = linkMatch[1];
        let inner: React.ReactNode = label;
        if (label.startsWith('**') && label.endsWith('**')) {
          inner = <strong>{label.slice(2, -2)}</strong>;
        } else if (label.startsWith('*') && label.endsWith('*')) {
          inner = <em>{label.slice(1, -1)}</em>;
        }
        // In-app paths (Library citations, /messages, ...) navigate client-side
        // in the same tab; only external URLs open a new tab.
        if (href.startsWith('/')) {
          return (
            <a
              key={i}
              href={href}
              onClick={(e) => {
                e.preventDefault();
                leaveAskIf(href);
              }}
              className="text-navy underline hover:text-navy-dark"
            >
              {inner}
            </a>
          );
        }
        return (
          <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-navy underline hover:text-navy-dark">
            {inner}
          </a>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <div className="relative flex h-full min-h-0 flex-1 lg:h-[calc(100vh-9rem-env(safe-area-inset-top))]">
      {/* Backdrop - mobile only when sidebar open */}
      {!isOnboardingMode && sidebarOpen && (
        <button
          type="button"
          aria-label="Close conversations"
          className="fixed inset-0 bg-black/30 z-10 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - overlay on mobile, in-flow on desktop (wrapper is 1rem wider
          than the card so a gap separates it from the chat panel) */}
      {!isOnboardingMode && (
        <div
          className={`fixed lg:relative inset-y-0 left-0 z-20 transform transition-transform duration-300 lg:transition-[width] lg:duration-300 lg:overflow-hidden flex-shrink-0 ${
            sidebarOpen ? 'translate-x-0 lg:w-[19rem]' : '-translate-x-full lg:w-0'
          }`}
        >
          <div className="w-72 h-full bg-white rounded-r-2xl lg:rounded-2xl shadow-sm flex flex-col">
            {/* Sidebar Header: Close (mobile) + New Conversation */}
            <div className="p-4 border-b border-gray-100 flex items-center gap-2">
              <button
                type="button"
                aria-label="Close"
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden p-2 -m-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              >
                <X size={20} />
              </button>
              <button
                onClick={startNewConversation}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-navy text-white rounded-xl hover:bg-navy-dark transition font-medium"
              >
                <Plus size={18} />
                New Conversation
              </button>
            </div>

            {/* Conversations List */}
            <div className="flex-1 overflow-y-auto p-2">
              {loadingConversations ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-navy"></div>
                </div>
              ) : conversations.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <MessageSquare className="mx-auto mb-2" size={32} />
                  <p className="text-sm">No conversations yet</p>
                </div>
              ) : (
                Object.entries(groupedConversations).map(([dateLabel, convs]) => (
                  <div key={dateLabel} className="mb-4">
                    <p className="px-2 py-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
                      {dateLabel}
                    </p>
                    {convs.map((conv) => (
                      <button
                        key={conv.id}
                        onClick={() => {
                          loadConversation(conv);
                          setSidebarOpen(false);
                        }}
                        className={`w-full text-left p-3 rounded-xl mb-1 group transition ${
                          conversationId === conv.id
                            ? 'bg-off-white border border-gold'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <p className="text-sm font-medium text-gray-800 line-clamp-2 flex-1 pr-2">
                            {conv.preview || 'New conversation'}
                          </p>
                          <button
                            onClick={(e) => deleteConversation(conv.id, e)}
                            className="opacity-100 p-1 pointer-coarse:p-[13px] pointer-coarse:-m-2 text-gray-400 hover:text-red-500 transition lg:pointer-fine:opacity-0 lg:pointer-fine:group-hover:opacity-100"
                            aria-label="Delete conversation"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                          <Clock size={12} />
                          <span>{conv.message_count} message{conv.message_count === 1 ? '' : 's'}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Card — header, messages, and input live in one aligned panel */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">
          {/* Card header */}
          <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-3 [@media(max-height:30rem)]:py-1.5 border-b border-gray-100">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {/* Back/close for mobile — desktop has the full app nav (L12) */}
              {!isOnboardingMode && (
                <button
                  type="button"
                  onClick={exitAskIf}
                  aria-label="Close Ask IF"
                  title="Close Ask IF"
                  className="p-2 -ml-1 rounded-lg text-gray-400 hover:text-navy hover:bg-off-white transition lg:hidden"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              {/* Conversations toggle (drawer on mobile, collapse on desktop) */}
              {!isOnboardingMode && (
                <button
                  type="button"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  aria-label={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
                  title={sidebarOpen ? 'Hide conversations' : 'Show conversations'}
                  className="p-2 rounded-lg text-gray-400 hover:text-navy hover:bg-off-white transition lg:-ml-1"
                >
                  {sidebarOpen ? (
                    <PanelLeftClose size={20} className="hidden lg:block" />
                  ) : (
                    <PanelLeftOpen size={20} className="hidden lg:block" />
                  )}
                  <MessageSquare size={20} className="lg:hidden" />
                </button>
              )}
              {isOnboardingMode ? (
                <div className="w-9 h-9 flex-shrink-0 bg-gold rounded-xl flex items-center justify-center">
                  <UserCircle className="text-white" size={22} />
                </div>
              ) : (
                <AskIfMark size={36} showLabel={false} tone="onLight" className="flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-base sm:text-lg font-bold text-navy truncate leading-tight">
                    {isOnboardingMode ? 'Profile Setup' : 'Ask IF'}
                  </h1>
                  {isAnalyticsMode && (
                    <span className="hidden sm:inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-navy/10 text-navy whitespace-nowrap">
                      Org analytics
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 hidden sm:block truncate">
                  {isOnboardingMode
                    ? 'Let Ask IF help you complete your profile'
                    : 'Your reflection & guidance companion'
                  }
                </p>
              </div>
            </div>

            {/* Onboarding progress or New chat */}
            {isOnboardingMode && onboardingFieldsStatus ? (
              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <p className="text-sm font-medium text-navy">
                    {onboardingFieldsStatus.completion_percentage}% Complete
                  </p>
                  <p className="text-xs text-gray-500 hidden sm:block">
                    {onboardingFieldsStatus.missing.length} fields remaining
                  </p>
                </div>
                <div className="w-16 sm:w-24 h-2 bg-gray-200 rounded-full overflow-hidden flex-shrink-0">
                  <div
                    className="h-full bg-gold rounded-full transition-all duration-500"
                    style={{ width: `${onboardingFieldsStatus.completion_percentage}%` }}
                  />
                </div>
              </div>
            ) : messages.length > 0 && !isOnboardingMode && (
              <button
                type="button"
                onClick={startNewConversation}
                className="flex items-center justify-center gap-1.5 px-3 py-2 shrink-0 text-sm font-medium text-gray-500 hover:text-navy hover:bg-off-white rounded-lg transition"
              >
                <RefreshCw size={15} />
                <span className="hidden sm:inline">New chat</span>
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="min-h-full flex flex-col items-center justify-center text-center p-4 sm:p-6">
                <div className="w-20 h-20 bg-off-white rounded-full flex items-center justify-center mb-4">
                  <Sparkles className="text-gold" size={40} />
                </div>
                <h2 className="text-xl font-semibold text-navy mb-2">
                  How can I help you reflect today?
                </h2>
                <p className="text-gray-500 max-w-md mb-6">
                  I'm here to help you think through your growth journey, surface relevant
                  teachings, and connect you with the right people.
                </p>

                {/* Quick Actions and Probing Questions */}
                <div className="w-full max-w-md space-y-5">
                  {/* Quick Action Buttons */}
                  <div className="flex flex-wrap justify-center gap-2">
                    {getQuickActions().map((qa, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => sendMessage(qa.action)}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-full hover:border-gold hover:bg-off-white transition text-sm font-medium text-navy"
                      >
                        {qa.label}
                      </button>
                    ))}
                  </div>

                  {/* Probing Questions */}
                  {probingQuestions.length > 0 && (
                    <div className="space-y-2 text-left">
                      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide text-center mb-3">
                        Or explore based on your snapshot
                      </p>
                      {probingQuestions.map((question, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => sendMessage(question)}
                          className="w-full flex items-start gap-2.5 text-left p-3 min-h-[44px] bg-off-white border border-transparent rounded-xl hover:border-gold hover:bg-white transition text-sm text-gray-700"
                        >
                          <Sparkles size={15} className="text-gold mt-0.5 shrink-0" />
                          <span>{question}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${
                      message.role === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    {message.role === 'assistant' && (
                      <AskIfMark size={32} showLabel={false} tone="onLight" className="flex-shrink-0" />
                    )}
                    <div className={`max-w-[85%] ${message.role === 'assistant' && membersFromAskIfAction(message.action).length ? 'sm:max-w-[80%]' : 'sm:max-w-[70%]'}`}>
                      <div
                        className={`p-4 rounded-2xl ${
                          message.role === 'user'
                            ? 'bg-navy text-white rounded-br-md'
                            : 'bg-off-white text-gray-800 rounded-bl-md'
                        }`}
                      >
                        <p className="whitespace-pre-wrap">{renderMessageContent(message.content)}</p>
                        {message.role === 'assistant' && (
                          <AskIfMemberCards members={membersFromAskIfAction(message.action)} />
                        )}
                      </div>
                    </div>
                    {message.role === 'user' && (
                      <div className="w-8 h-8 bg-gold rounded-lg flex items-center justify-center flex-shrink-0">
                        <User className="text-white" size={18} />
                      </div>
                    )}
                  </div>
                ))}

                {/* Action Buttons for Draft Message */}
                {pendingAction?.type === 'draft_message' && (
                  <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3 mt-4">
                    <button
                      type="button"
                      onClick={handleSendDraft}
                      disabled={actionLoading}
                      className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                      {actionLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      ) : (
                        <Check size={18} />
                      )}
                      Send Now
                    </button>
                    <button
                      type="button"
                      onClick={handleEditDraft}
                      disabled={actionLoading}
                      className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                    >
                      <Edit size={18} />
                      Edit
                    </button>
                  </div>
                )}

                {/* Invite Member form - dropped into chat by Jarvis */}
                {inviteForm?.type === 'invite_form' && (
                  <div className="flex gap-3">
                    <AskIfMark size={32} showLabel={false} tone="onLight" className="flex-shrink-0" />
                    <form
                      onSubmit={handleSubmitInvite}
                      className="max-w-[85%] sm:max-w-[70%] w-full bg-white border border-gold rounded-2xl rounded-bl-md p-4 space-y-3"
                    >
                      <div className="flex items-center gap-2 text-navy font-semibold">
                        <UserPlus size={18} className="text-gold" />
                        Invite a new member
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          required
                          value={inviteData.email}
                          onChange={(e) => setInviteData((d) => ({ ...d, email: e.target.value }))}
                          placeholder="name@company.com"
                          className="w-full min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">First name</label>
                          <input
                            type="text"
                            value={inviteData.first_name}
                            onChange={(e) => setInviteData((d) => ({ ...d, first_name: e.target.value }))}
                            className="w-full min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Last name</label>
                          <input
                            type="text"
                            value={inviteData.last_name}
                            onChange={(e) => setInviteData((d) => ({ ...d, last_name: e.target.value }))}
                            className="w-full min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm"
                          />
                        </div>
                      </div>

                      {/* Role - only when the user can pick more than one */}
                      {(inviteForm.allowed_roles?.length ?? 0) > 1 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                          <select
                            value={inviteData.role}
                            onChange={(e) => setInviteData((d) => ({ ...d, role: e.target.value }))}
                            className="w-full min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm bg-white"
                          >
                            {inviteForm.allowed_roles?.map((r) => (
                              <option key={r} value={r}>
                                {roleLabel(r)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Forum - shown when forums are available */}
                      {(inviteForm.forums?.length ?? 0) > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Forum{inviteForm.forum_required ? ' *' : ' (optional)'}
                          </label>
                          <select
                            value={inviteData.forum_id}
                            onChange={(e) => setInviteData((d) => ({ ...d, forum_id: e.target.value }))}
                            className="w-full min-h-[44px] px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none text-sm bg-white"
                          >
                            {!inviteForm.forum_required && <option value="">No Forum yet</option>}
                            {inviteForm.forum_required && <option value="">Choose a Forum…</option>}
                            {inviteForm.forums?.map((f) => (
                              <option key={f.id} value={f.id}>
                                {f.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {inviteError && (
                        <p className="text-sm text-red-600">{inviteError}</p>
                      )}

                      <div className="flex flex-col sm:flex-row gap-2 pt-1">
                        <button
                          type="submit"
                          disabled={inviteLoading}
                          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-navy text-white rounded-lg hover:bg-navy-dark transition disabled:opacity-50"
                        >
                          {inviteLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                          ) : (
                            <Send size={16} />
                          )}
                          Send Invitation
                        </button>
                        <button
                          type="button"
                          onClick={() => setInviteForm(null)}
                          disabled={inviteLoading}
                          className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Undo Hint - shows after undoable action */}
                {showUndoHint && recentActions.length > 0 && (
                  <div className="flex justify-center mt-2">
                    <button
                      type="button"
                      onClick={handleUndo}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-500 hover:text-navy hover:bg-gray-100 rounded-lg transition"
                    >
                      <Undo2 size={14} />
                      <span>Say "undo" to reverse that action</span>
                    </button>
                  </div>
                )}

                {isLoading && (
                  <div className="flex gap-3">
                    <AskIfMark size={32} showLabel={false} tone="onLight" className="flex-shrink-0" />
                    <div className="bg-off-white p-4 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1">
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '0ms' }}
                        />
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '150ms' }}
                        />
                        <div
                          className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                          style={{ animationDelay: '300ms' }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Onboarding Complete Action Card */}
                {isOnboardingMode && onboardingComplete && (
                  <div className="mt-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-6 text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="text-green-600" size={32} />
                    </div>
                    <h3 className="text-lg font-semibold text-green-800 mb-2">
                      Profile Complete!
                    </h3>
                    <p className="text-green-600 mb-4">
                      Your profile is now visible to other members. Start connecting with the community!
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-3">
                      <button
                        type="button"
                        onClick={() => navigate('/members')}
                        className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
                      >
                        View Members
                        <ArrowRight size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/members/${user?.id}`)}
                        className="flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition"
                      >
                        View My Profile
                      </button>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input Area */}
          <form onSubmit={handleSubmit} className="p-2 sm:p-4 [@media(max-height:30rem)]:p-2 border-t border-gray-100">
            <div className="flex gap-2 sm:gap-3">
              <div className="relative flex-1">
                <AutosizeTextarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit();
                    }
                  }}
                  placeholder="Ask IF anything..."
                  name="if-ask"
                  autoComplete="off"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  spellCheck
                  data-lpignore="true"
                  data-1p-ignore="true"
                  maxHeight={160}
                  className="w-full min-h-[44px] px-3 sm:px-4 py-3 pr-12 rounded-xl border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none"
                  disabled={isLoading}
                />
                <MicButton
                  value={input}
                  onChange={setInput}
                  className="absolute bottom-2 right-2"
                />
              </div>
              <button
                type="submit"
                disabled={!input.trim() || isLoading}
                className="flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center px-4 py-3 bg-navy text-white rounded-xl hover:bg-navy-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <Send size={20} />
              </button>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-400 mt-2 text-center [@media(max-height:30rem)]:hidden">
              Ask IF helps with reflection and surfaces Iron Forums content. For personal
              guidance, connect with your facilitator.
            </p>
          </form>
        </div>
      </div>

      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={handleConfirmDeleteConversation}
        onCancel={() => { setDeleteConfirmOpen(false); setPendingDeleteConvId(null); }}
        title="Delete Conversation?"
        message="This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}
